import { useEffect, useState } from "react";
import {
    KeyRound, Plus, Loader2, RefreshCcw, Trash2, ShieldCheck, Lock,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const TIPO_OPTS = [
    { value: "bearer_token", label: "Bearer Token" },
    { value: "api_key", label: "API Key" },
    { value: "password", label: "Password" },
    { value: "ssh_key", label: "SSH Key" },
    { value: "oauth_client_secret", label: "OAuth Client Secret" },
    { value: "hmac_secret", label: "HMAC Secret (webhook)" },
];

function fmt(iso) {
    if (!iso) return "—";
    return iso.slice(0, 16).replace("T", " ");
}

export default function CredentialsVault() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [rotarTarget, setRotarTarget] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/vault/credentials");
            setRows(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sin acceso al vault");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const eliminar = async (c) => {
        if (!confirm(`Eliminar "${c.nombre_descriptivo}"? Solo si no está en uso.`)) return;
        try {
            await api.delete(`/vault/credentials/${c.id}`);
            toast.success("Credencial eliminada");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <div className="p-8 lg:p-10 max-w-[1400px] space-y-6" data-testid="vault-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2 flex items-center gap-2">
                        <Lock className="h-3 w-3" /> Seguridad · Fase 7 · A2
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <KeyRound className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Vault de credenciales
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Credenciales encriptadas con Fernet para conectar con originadores externos.
                        <span className="ml-1 font-medium text-[color:var(--brand-dark-gold)]">
                            Los valores nunca se muestran tras creación.
                        </span>{" "}
                        Solo pueden rotarse o eliminarse (si no están en uso).
                    </p>
                </div>
                <CrearCredencialDialog
                    open={openCreate} setOpen={setOpenCreate}
                    reload={load}
                />
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="vault-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Tipo</th>
                            <th>En uso por</th>
                            <th>Rotada</th>
                            <th>Creada</th>
                            <th>Creada por</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c) => (
                            <tr key={c.id} data-testid={`vault-row-${c.id}`}>
                                <td>
                                    <div className="font-medium">{c.nombre_descriptivo}</div>
                                    <div className="hash-cell text-[10px] text-muted-foreground">{c.id.slice(0, 16)}…</div>
                                </td>
                                <td>
                                    <span className="badge-gold text-[10px]">{c.tipo}</span>
                                </td>
                                <td>
                                    {(c.en_uso_por || []).length > 0 ? (
                                        <span className="badge-mock text-[10px]" title={(c.en_uso_por || []).join(", ")}>
                                            <ShieldCheck className="h-3 w-3" />
                                            {(c.en_uso_por || []).length} fuente(s)
                                        </span>
                                    ) : (
                                        <span className="text-xs text-muted-foreground italic">libre</span>
                                    )}
                                </td>
                                <td className="tabular text-xs">{fmt(c.rotacion_fecha)}</td>
                                <td className="tabular text-xs">{fmt(c.created_at)}</td>
                                <td className="text-xs">{c.created_by}</td>
                                <td>
                                    <div className="flex gap-1">
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => setRotarTarget(c)}
                                            data-testid={`vault-rotar-${c.id}`}
                                            title="Rotar credencial"
                                        >
                                            <RefreshCcw className="h-3 w-3" />
                                        </Button>
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => eliminar(c)}
                                            data-testid={`vault-delete-${c.id}`}
                                            title="Eliminar"
                                            disabled={(c.en_uso_por || []).length > 0}
                                        >
                                            <Trash2 className="h-3 w-3 text-[color:var(--brand-coral)]" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && !loading && (
                            <tr>
                                <td colSpan={7} className="text-center py-10 text-muted-foreground">
                                    Sin credenciales. Crea la primera para conectar orígenes externos.
                                </td>
                            </tr>
                        )}
                        {loading && (
                            <tr>
                                <td colSpan={7} className="text-center py-6">
                                    <Loader2 className="h-4 w-4 animate-spin inline" />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {rotarTarget && (
                <RotarDialog
                    cred={rotarTarget}
                    onClose={() => setRotarTarget(null)}
                    reload={load}
                />
            )}
        </div>
    );
}


function CrearCredencialDialog({ open, setOpen, reload }) {
    const [tipo, setTipo] = useState("bearer_token");
    const [nombre, setNombre] = useState("");
    const [valor, setValor] = useState("");
    const [expira, setExpira] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!nombre.trim() || !valor.trim()) {
            return toast.error("Nombre y valor son requeridos");
        }
        setBusy(true);
        try {
            await api.post("/vault/credentials", {
                tipo, nombre_descriptivo: nombre, valor,
                expira_at: expira || null,
            });
            toast.success("Credencial creada · el valor original no se puede recuperar");
            setOpen(false);
            setNombre(""); setValor(""); setExpira("");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="vault-crear-btn">
                    <Plus className="h-4 w-4 mr-2" /> Nueva credencial
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Nueva credencial</DialogTitle>
                    <DialogDescription>
                        El valor se encripta con Fernet y no puede recuperarse tras
                        guardar — solo rotarse. Copia el valor original si lo necesitas.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Tipo</Label>
                        <Select value={tipo} onValueChange={setTipo}>
                            <SelectTrigger data-testid="vault-tipo-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {TIPO_OPTS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Nombre descriptivo</Label>
                        <Input
                            value={nombre} onChange={(e) => setNombre(e.target.value)}
                            placeholder="Ej: Originador ABC · Producción"
                            data-testid="vault-nombre-input"
                        />
                    </div>
                    <div>
                        <Label>Valor secreto</Label>
                        <Input
                            type="password" value={valor}
                            onChange={(e) => setValor(e.target.value)}
                            placeholder="********"
                            autoComplete="new-password"
                            data-testid="vault-valor-input"
                        />
                    </div>
                    <div>
                        <Label>Expira (opcional, YYYY-MM-DD)</Label>
                        <Input
                            value={expira} onChange={(e) => setExpira(e.target.value)}
                            placeholder="2026-12-31"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button onClick={submit} disabled={busy} data-testid="vault-submit-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RotarDialog({ cred, onClose, reload }) {
    const [valor, setValor] = useState("");
    const [motivo, setMotivo] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!valor.trim() || motivo.trim().length < 3) {
            return toast.error("Nuevo valor y motivo (>=3 chars) son requeridos");
        }
        setBusy(true);
        try {
            await api.post(`/vault/credentials/${cred.id}/rotar`, {
                nuevo_valor: valor, motivo,
            });
            toast.success("Credencial rotada");
            onClose();
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="vault-rotar-dialog">
                <DialogHeader>
                    <DialogTitle>Rotar credencial</DialogTitle>
                    <DialogDescription>
                        Reemplaza el valor secreto. La versión anterior no se recupera.
                        Todas las fuentes que la usen empezarán a autenticar con el nuevo valor
                        en la próxima ingesta.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="text-sm">
                        <span className="text-muted-foreground">Credencial: </span>
                        <span className="font-medium">{cred.nombre_descriptivo}</span>
                    </div>
                    <div>
                        <Label>Nuevo valor</Label>
                        <Input
                            type="password" value={valor}
                            onChange={(e) => setValor(e.target.value)}
                            autoComplete="new-password"
                            data-testid="vault-rotar-valor-input"
                        />
                    </div>
                    <div>
                        <Label>Motivo (auditoría)</Label>
                        <Input
                            value={motivo} onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Ej: rotación trimestral programada"
                            data-testid="vault-rotar-motivo-input"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    <Button onClick={submit} disabled={busy} data-testid="vault-rotar-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rotar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
